import { useAuthenticator } from "@aws-amplify/ui-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, SecretTextField } from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";

import { AuthScreenLayout } from "./AuthScreenLayout";
import { forceNewPasswordSchema, type ForceNewPasswordValues } from "./schema";

/**
 * From `password_policy` in platform's `infra/platform/cognito.tf` — literals,
 * not Terraform variables, so this copy can't drift per deployment.
 */
const PASSWORD_RULES =
  "At least 8 characters, including upper and lower case, a number, and a symbol.";

/**
 * Cognito's `NEW_PASSWORD_REQUIRED` challenge, which every admin-invited user
 * hits on first sign-in.
 */
export function ForceNewPasswordScreen() {
  const { submitForm, toSignIn, error, validationErrors, isPending } =
    useAuthenticator((context) => [
      context.submitForm,
      context.toSignIn,
      context.error,
      context.validationErrors,
      context.isPending,
    ]);

  const { control, handleSubmit } = useForm<ForceNewPasswordValues>({
    resolver: zodResolver(forceNewPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  // The schema catches the mismatch that Amplify reports via validationErrors
  // rather than error, which would otherwise fail silently.
  const onValid = ({ password, confirmPassword }: ForceNewPasswordValues) => {
    submitForm({ password, confirm_password: confirmPassword });
  };

  // Amplify splits failures across error and validationErrors; show both.
  const messages = [
    ...new Set(
      [
        typeof error === "string" ? error : null,
        ...Object.values(validationErrors ?? {}).flat(),
      ].filter((m): m is string => Boolean(m)),
    ),
  ];

  return (
    <AuthScreenLayout
      subtitle="Your account was set up with a temporary password. Choose a permanent one to continue."
      title="Choose a password"
    >
      <form
        className="flex flex-col gap-lg"
        onSubmit={handleSubmit(onValid)}
        noValidate
      >
        <Controller
          control={control}
          name="password"
          render={({ field, fieldState }) => (
            <SecretTextField
              assistiveText={fieldState.error?.message ?? PASSWORD_RULES}
              inputProps={{
                autoComplete: "new-password",
                className: "bg-background-primary",
              }}
              isInvalid={fieldState.invalid}
              label="New password"
              isRequired
              {...field}
            />
          )}
          rules={{ deps: ["confirmPassword"] }}
        />
        <Controller
          control={control}
          name="confirmPassword"
          render={({ field, fieldState }) => (
            <SecretTextField
              assistiveText={fieldState.error?.message}
              inputProps={{
                autoComplete: "new-password",
                className: "bg-background-primary",
              }}
              isInvalid={fieldState.invalid}
              label="Confirm password"
              isRequired
              {...field}
            />
          )}
          rules={{ deps: ["password"] }}
        />

        {messages.length > 0 ? (
          <div className="flex flex-col gap-xxs" role="alert">
            {messages.map((m) => (
              <p className="text-label-medium text-foreground-negative" key={m}>
                {m}
              </p>
            ))}
          </div>
        ) : null}

        <Button
          className="flex items-center justify-center"
          data-testid="force-new-password-submit"
          isPending={isPending}
          type="submit"
          variant="primary"
        >
          Set password
        </Button>

        <div className="flex items-center justify-center">
          <Button
            className="h-auto p-0 font-normal"
            onPress={toSignIn}
            variant="link"
          >
            Back to sign in
          </Button>
        </div>
      </form>
    </AuthScreenLayout>
  );
}
